import { Locales } from "@grandvisionhq/state";
import * as utils from "@grandvisionhq/utils";
import { createAltTag } from "../../utils/utils/create-alt-tag.js";
import { createProductTypeName } from "../../utils/utils/create-product-type-name.js";
import { getDiscount } from "../../utils/utils/get-discount.js";
import { getSearchLocalizedValue, getSearchJoinedLocalizedValues } from "../../utils/utils/get-search-localized-value.js";
const ENGLISH = Locales["en-GB"];
const DEFAULT_PRICE_SEGMENT = "no-channel";
const mapListingProductRecordToListingProduct = (product, intl, priceSegment) => {
  const { getLabel, locale } = intl;
  const labels = {
    lens: getLabel(`products.unit.contactLens`),
    lenses: getLabel(`products.unit.contactLenses`),
    perBox: getLabel(`products.perBox`)
  };
  const slug = product.slug;
  const masterVariant = mapVariant(product, locale, labels, void 0, priceSegment);
  const productType = createProductTypeName(product.productType);
  if (!slug || !product.objectID || !productType || !masterVariant) {
    console.error(
      "No slug, objectId, productType or masterVariant found for product sku: ",
      product.sku
    );
    return void 0;
  }
  const variants = product.variants?.map((variant) => mapVariant(product, locale, labels, variant, priceSegment)).filter(utils.isDefined) ?? [];
  const firstCategory = product.categories[0];
  const category = firstCategory ? getSearchLocalizedValue(firstCategory, "category_name", ENGLISH) : void 0;
  return {
    category,
    includedVatAmount: product.includedVatAmount,
    isOnStock: product.isOnStock,
    masterVariant,
    name: {
      en: getSearchLocalizedValue(product, "name", ENGLISH) ?? "",
      localized: getSearchLocalizedValue(product, "name", locale) ?? ""
    },
    objectID: product.objectID,
    offering: void 0,
    // missing
    productType,
    ratingsSummary: void 0,
    // missing
    slug,
    variants,
    isOfferedWithPrescriptions: Boolean(product.isOfferedWithPrescriptions)
  };
};
const mapVariant = (product, locale, labels, variant, priceSegment) => {
  const altTag = createAltTag(product, locale, labels);
  const pricing = mapPricing(product, locale, variant, priceSegment);
  if (variant && !variant.brandModel && !variant.images?.length && !variant.sku) {
    console.error("Variant found with insufficiant basic info for product: ", product.sku);
    return void 0;
  }
  if (!product.brand || !product.brandModel || !product.brandModelColorCode || !product.images?.length || // !product.erpId ||
  !product.sku) {
    console.error("product found with insufficiant basic info: ", product.sku);
    return void 0;
  }
  if (!pricing) {
    console.error("Insufficiant pricing info for product: ", product.sku);
    return void 0;
  }
  const frame = {
    color: {
      en: getSearchJoinedLocalizedValues(product, "frameColor", ENGLISH),
      localized: getSearchJoinedLocalizedValues(product, "frameColor", locale)
    },
    material: { en: getSearchLocalizedValue(product, "frameMaterial", ENGLISH) },
    shape: { en: getSearchLocalizedValue(product, "frameShape", ENGLISH) },
    type: { en: getSearchLocalizedValue(product, "frameType", ENGLISH) }
  };
  const images = variant ? variant.images.map((img) => ({ ...img, altTag })) : product.images.map((img) => ({ ...img, altTag }));
  const tagKey = variant ? variant.tags?.[0]?.key : product.tags?.[0]?.key;
  const tagValue = variant ? variant.tags?.[0]?.value : product.tags?.[0]?.value;
  return {
    brand: {
      name: product.brand,
      model: variant ? variant.brandModel : product.brandModel,
      modelCode: variant ? variant.brandModelCode : product.brandModelCode
    },
    erpId: "product.erpId",
    // missing
    description: getSearchLocalizedValue(product, "description", locale) ?? "",
    frame,
    faceShape: { en: getSearchJoinedLocalizedValues(product, "faceShape", ENGLISH) },
    gender: { en: getSearchJoinedLocalizedValues(product, "targetGender", ENGLISH) },
    lensColor: getSearchLocalizedValue(product, "lensColor", locale),
    lensType: { en: getSearchLocalizedValue(product, "lensType", ENGLISH) },
    replacementFrequency: getSearchLocalizedValue(product, "replacementFrequency", locale),
    material: getSearchLocalizedValue(product, "material", locale),
    accessoryType: {
      en: getSearchLocalizedValue(product, "accessoryType", ENGLISH),
      localized: getSearchLocalizedValue(product, "accessoryType", locale)
    },
    accessoryColor: {
      en: getSearchJoinedLocalizedValues(product, "accessoryColor", ENGLISH),
      localized: getSearchJoinedLocalizedValues(product, "accessoryColor", locale)
    },
    contactLensCareType: getSearchLocalizedValue(product, "contactLensCareType", locale),
    isInsuranceReimbursable: Boolean(product.isInsuranceReimbursable),
    power: product.power,
    priceIncludedAmount: product.priceIncludedAmount,
    packageOption: product.packageOption,
    packageQuantity: product.packageQuantity,
    solutionType: product.solutionType,
    isExclusiveBrand: product.isExclusiveBrand,
    name: product.name_en ?? "",
    // becoming unlocalized
    sku: variant ? variant.sku : product.sku,
    tag: { key: tagKey, value: tagValue },
    images,
    pricing,
    consolidationFacet: null
  };
};
const mapPricing = (product, locale, variant, priceSegment) => {
  const productPriceMap = product?.prices;
  const variantPriceMap = variant?.prices;
  const defaultProductPrice = productPriceMap?.[DEFAULT_PRICE_SEGMENT];
  if (variantPriceMap || productPriceMap) {
    const segmentPrice = getSegmentPrice(variantPriceMap ?? productPriceMap, priceSegment);
    if (!defaultProductPrice || !segmentPrice.priceAmount || !segmentPrice.priceCurrency)
      return void 0;
    return {
      bestPrice: defaultProductPrice?.bestPrice,
      price: {
        isCentAmount: false,
        originalPrice: segmentPrice.priceAmount,
        currencyCode: segmentPrice.priceCurrency,
        discount: defaultProductPrice.discountedPrice ? {
          description: getSearchLocalizedValue(
            defaultProductPrice,
            "discountDescription",
            locale
          ),
          price: defaultProductPrice.discountedPrice,
          badge: getDiscount(
            locale,
            defaultProductPrice.discountedPriceCurrencyCode,
            defaultProductPrice.discountType,
            defaultProductPrice.discountedPrice
          ),
          validFrom: defaultProductPrice.discountValidFrom,
          validUntil: defaultProductPrice.discountValidUntil
        } : void 0
      },
      pricePerUnit: {
        amount: defaultProductPrice.pricePerUnitAmount,
        currencyCode: defaultProductPrice.pricePerUnitCurrency
      }
    };
  }
  const originalPrice = variant ? variant.priceAmount : product.priceAmount;
  const currencyCode = variant ? variant.priceCurrency : product.priceCurrency;
  if (!originalPrice || !currencyCode) return void 0;
  return {
    bestPrice: product.bestPrice,
    price: {
      isCentAmount: false,
      originalPrice,
      currencyCode,
      discount: product.discountedPrice ? {
        description: getSearchLocalizedValue(product, "discountDescription", locale),
        price: product.discountedPrice,
        badge: getDiscount(
          locale,
          product.discountedPriceCurrencyCode,
          product.discountType,
          product.discountedPrice
        ),
        validFrom: product.discountValidFrom,
        validUntil: product.discountValidUntil
      } : void 0
    },
    pricePerUnit: {
      amount: product.pricePerUnitAmount,
      currencyCode: product.pricePerUnitCurrency
    }
  };
};
const getSegmentPrice = (priceMap, priceSegment) => {
  if (priceMap && priceSegment && priceSegment in priceMap) {
    const price = priceMap[priceSegment];
    return {
      priceAmount: price.priceAmount,
      priceCurrency: price.priceCurrency
    };
  } else {
    const price = priceMap?.[DEFAULT_PRICE_SEGMENT];
    return {
      priceAmount: price?.priceAmount,
      priceCurrency: price?.priceCurrency
    };
  }
};
export {
  mapListingProductRecordToListingProduct,
  mapVariant
};
//# sourceMappingURL=map-listing-product-record-to-listing-product.js.map
